import React, { useContext } from 'react'
import CellDisplayList from '@Components/ViewWork/Table/ValidationCells/CellDisplayList'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import usePublisherService from '@App/services/usePublisherService'
import { TableInfo, TableRowData } from '@Components/ViewWork/Table'
import { TOOLTIP_TEXT } from '@Const/tooltip-text'
import CustomToolTip from '@Components/Tooltips/CustomToolTip'
import { canAmend } from '@App/helpers/permission'
import { tableCellSanitizer } from '@App/helpers/table-cell-sanitizer'
import {
    generateAdminTableCells,
    generatePublisherTableCells,
} from '@App/helpers/work-entities/utils/add-publisher-table-cells'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'

const AddPublishersTableInfo = (): TableInfo[] => {
    const { workStore, publisherStepStore } = useContext<RootStore>(StoresContext)

    const workDraftFromLocal = workStore.workDraftLocal

    if (!workDraftFromLocal) {
        return []
    }

    const originPublishers = workDraftFromLocal.originalPublishers

    const publisherService = usePublisherService()

    const keyPrefix = (model: OriginalPublisherModel | PublisherModel) =>
        `${model.publisherName}-${model.localId}-${model.ipiNumber ?? ''}-${model.ipId ?? ''}`

    const adminTableRows = (
        rows: Array<{
            originalPublisherIndex: number
            administratorIndex: number
            publisherModel: OriginalPublisherModel
            adminModel: PublisherModel
        }>
    ): TableRowData[] =>
        rows.map(({ originalPublisherIndex, administratorIndex, publisherModel, adminModel }, index) => ({
            removeCellIdentifier: `${originalPublisherIndex.toString()}-${administratorIndex.toString()}`,
            removable: canAmend(adminModel.amend),
            cells: generateAdminTableCells({
                admin: adminModel,
                index: index,
                publisher: publisherModel,
                getKeyPrefix: (publisher) => keyPrefix(publisher),
            }),
            subTables: [
                {
                    headerRow: {
                        row: [
                            { text: 'Publisher Name' },
                            { text: 'Publisher IPI' },
                            { text: 'Publisher Number' },
                            {
                                text: 'Represented Writers',
                                tooltip: (
                                    <CustomToolTip
                                        inverse
                                        type="?"
                                        place="top"
                                        info={TOOLTIP_TEXT.createWork.representedWriter}
                                    />
                                ),
                            },
                        ],
                    },
                    rows: [
                        {
                            removeCellIdentifier: publisherModel.ipId ?? '',
                            removable: canAmend(adminModel.amend),
                            cells: [
                                tableCellSanitizer(publisherModel.publisherName),
                                tableCellSanitizer(publisherModel.ipiNumber),
                                tableCellSanitizer(publisherModel.hfaPublisherNumber),
                                <CellDisplayList
                                    readOnly={!canAmend(adminModel.amend)}
                                    key={`${keyPrefix(publisherModel)}-display-list`}
                                    items={publisherModel.writers}
                                    onAddToListClicked={() => {
                                        publisherStepStore.initAddWriterToOriginalPublisher(publisherModel)
                                    }}
                                    onRemoveFromListClicked={(writerModel) => {
                                        publisherService.removeLinkedWriter(publisherModel, writerModel)
                                    }}
                                />,
                            ],
                        },
                    ],
                },
            ],
        }))

    const admins: Array<{
        originalPublisherIndex: number
        administratorIndex: number
        publisherModel: OriginalPublisherModel
        adminModel: PublisherModel
    }> = []

    const nonAdminsPubs: TableRowData[] = []

    originPublishers.forEach((model, index) => {
        const hasAdmin: boolean = model.administratorPublishers.length > 0

        if (hasAdmin) {
            model.administratorPublishers.forEach((admin, adminIndex) => {
                admins.push({
                    originalPublisherIndex: index,
                    administratorIndex: adminIndex,
                    publisherModel: model,
                    adminModel: admin,
                })
            })
        } else {
            nonAdminsPubs.push({
                removeCellIdentifier: index.toString() ?? '',
                removable: canAmend(model.amend),
                cells: generatePublisherTableCells({
                    publisher: model,
                    index: index,
                    getKeyPrefix: (publisher) => keyPrefix(publisher),
                }),
            })
        }
    })

    const adminRows: TableRowData[] = adminTableRows(admins)

    return [
        {
            headerRow: {
                row: [
                    {
                        text: 'Administrator',
                        tooltip: (
                            <CustomToolTip inverse place="top" type="?" info={TOOLTIP_TEXT.createWork.administrator} />
                        ),
                    },
                    { text: 'Administrator IPI' },
                    { text: 'Administrator Number' },
                    { text: 'Collection Share' },
                ],
                removableCells: true,
            },
            rows: [...adminRows],
        },
        {
            headerRow: {
                row: [
                    { text: 'Publisher Name' },
                    { text: 'Publisher IPI' },
                    { text: 'Publisher Number' },
                    { text: 'Collection Share' },
                    { text: 'Add Administrator' },
                    {
                        text: 'Represented Writers',
                        tooltip: (
                            <CustomToolTip
                                inverse
                                place="top"
                                type="?"
                                info={TOOLTIP_TEXT.createWork.representedWriter}
                            />
                        ),
                    },
                ],
                removableCells: true,
            },
            rows: nonAdminsPubs,
        },
    ]
}

export default AddPublishersTableInfo
